import * as THREE from "three";
import { hashSeed, wearsSteampack, type Gear, type HeldTool, type Look } from "@dyson/shared";
import { disposeLabel, makeLabel } from "./Label";
import { RigMesh } from "./rig/RigMesh";
import { BODY_SCALE, CrewAnimator, crewModel, crewPalette, crewTraits, type CharacterPose } from "./rig/crew";

export type { CharacterMode, CharacterPose } from "./rig/crew";

/** How finely the figure is baked: pixels a figure metre (its pixel art, 64 a metre, doubled). */
const PX = 64 / BODY_SCALE;

/**
 * A crew member (see rig/crew.ts): the rig, rebuilt when what they wear or
 * hold changes, posed procedurally from how they are moving. The origin is
 * at the feet; the body mirrors to face left, so "forward" is always +X in
 * its own space.
 */
export class CharacterMesh {
  readonly group = new THREE.Group();
  private readonly body = new THREE.Group();
  private rig: RigMesh | null = null;
  private rigKey = "";
  private readonly anim = new CrewAnimator();
  /** Skin and hair colour: fixed by their name. */
  private readonly traits: ReturnType<typeof crewTraits>;
  private readonly label: THREE.Sprite;
  private lookKey: string | null = null;
  private hue: number;
  private hat: Look["hat"] = "none";
  private build: Look["body"] = "standard";
  private hair: Look["hair"] = "crop";
  private held: HeldTool | null = null;
  /** A steampack on their back (nobody starts with one). */
  private pack = false;

  /** Displayed position relative to `frame` (or world when frame is null). */
  readonly local = new THREE.Vector3();
  frame: string | null = null;

  constructor(name: string, look?: Look) {
    this.hue = (hashSeed(name) % 360) / 360;
    this.traits = crewTraits(name);
    this.setLook(look);
    this.body.position.z = 0.2;
    this.group.add(this.body);

    this.label = makeLabel(name, 0.6);
    this.label.position.y = 2.3;
    this.group.add(this.label);
    // Built to a 1.7-tall figure; scaled to the character's real size (in blocks).
    this.group.scale.setScalar(1 / BODY_SCALE);
  }

  /** Show or hide the name over their head (hidden when zoomed far out, and in photo mode). */
  setLabelVisible(visible: boolean): void {
    this.label.visible = visible;
  }

  /** Dress them as `look` says (coat colour, hat, build, hair); nothing changes if it's what they already wear. */
  setLook(look: Look | undefined): void {
    const key = look ? `${look.hue}:${look.hat}:${look.body}:${look.hair}` : "";
    if (key === this.lookKey) return;
    this.lookKey = key;
    if (look) this.hue = look.hue / 360;
    this.hat = look?.hat ?? "none";
    this.build = look?.body ?? "standard";
    this.hair = look?.hair ?? "crop";
    this.anim.build = this.build;
    this.dress();
  }

  /** Dress them in what they wear: for now, whether a steampack is on their back. */
  setGear(gear: Gear | undefined): void {
    const pack = wearsSteampack(gear);
    if (pack === this.pack) return;
    this.pack = pack;
    this.dress();
  }

  /** Swing once (placing a block). A swing already under way finishes first. */
  swing(): void {
    this.anim.swing();
  }

  animate(dt: number, pose: CharacterPose): void {
    if (pose.tool?.kind !== this.held?.kind || pose.tool?.tier !== this.held?.tier) {
      this.held = pose.tool;
      this.dress();
    }
    const p = this.anim.step(dt, pose);
    this.body.scale.set(this.anim.facing, 1 - this.anim.squash, 1 + this.anim.squash * 0.5);
    this.rig?.pose(p, 1);
  }

  /** Build the rig for what they're wearing and holding, if that's changed. */
  private dress(): void {
    const key = `${this.hue}|${this.hat}|${this.hair}|${this.build}|${this.held?.kind}|${this.held?.tier}|${this.pack}`;
    if (key === this.rigKey) return;
    this.rigKey = key;
    if (this.rig) {
      this.body.remove(this.rig.group);
      this.rig.dispose();
    }
    this.rig = new RigMesh(crewModel(this.hair, this.hat, this.held?.kind ?? null, this.build, this.pack), crewPalette(this.hue, this.traits, this.held?.tier), PX);
    this.body.add(this.rig.group);
  }

  dispose(): void {
    this.rig?.dispose();
    disposeLabel(this.label);
  }
}
